import {
  createEffect,
  createEvent,
  createStore,
  sample,
  type EventCallable,
} from 'effector'
import { model as sessionModel } from '~/entities/session'
import { preferencesFeatureModel } from '~/features/preferences'
import { secondsToRoundedMinutesConsumed } from '~/shared/helpers'
import { logAppsFlyerInAppEvent } from './adapter'

export const init: EventCallable<void> = createEvent()

export type AppsFlyerContentEventData = {
  af_content_id: string // contentId
  af_content_type: 'movie' | 'tvshow' | 'live' // contentType
  af_content: string // contentTitle
}

export type AppsFlyerMediaPlayPayload = AppsFlyerContentEventData & {
  af_customer_user_id: string // userGuid
  user_region: string // regionTitle
}

export type AppsFlyerMediaMinutesPayload = AppsFlyerContentEventData & {
  minutes_consumed: number
  af_customer_user_id: string
  user_region: string
}

export const sendMediaPlay = createEvent<AppsFlyerContentEventData>()

const logMediaPlayFx = createEffect((payload: AppsFlyerMediaPlayPayload) => {
  logAppsFlyerInAppEvent('media_play', payload)
})

sample({
  clock: sendMediaPlay,
  source: {
    sessionInfo: sessionModel.$info,
    preference: preferencesFeatureModel.$preference,
  },
  fn: ({ sessionInfo, preference }, eventData) => ({
    ...eventData,
    af_customer_user_id: sessionInfo?.userGuid ?? '',
    user_region: preference?.name ?? '',
  }),
  target: logMediaPlayFx,
})

/** Seconds since last `sendMediaPlay`, incremented every wall second until `sendMediaMinutes` effect finishes. */
const playbackSecondTick = createEvent()

export const $secondsWatchedSinceMediaPlay = createStore(0)
  .on(playbackSecondTick, (s) => s + 1)
  .reset(sendMediaPlay)

let playbackTickHandle: ReturnType<typeof setInterval> | null = null

const startPlaybackSecondTickerFx = createEffect(() => {
  if (typeof window === 'undefined') return
  if (playbackTickHandle !== null) {
    clearInterval(playbackTickHandle)
  }
  playbackTickHandle = setInterval(() => {
    playbackSecondTick()
  }, 1000)
})

sample({
  clock: sendMediaPlay,
  target: startPlaybackSecondTickerFx,
})

export const sendMediaMinutes = createEvent<AppsFlyerContentEventData>()

const logMediaMinutesFx = createEffect(
  (payload: AppsFlyerMediaMinutesPayload) => {
    logAppsFlyerInAppEvent('media_minutes', payload)
  }
)

$secondsWatchedSinceMediaPlay.reset(logMediaMinutesFx.finally)

sample({
  clock: sendMediaMinutes,
  source: {
    seconds: $secondsWatchedSinceMediaPlay,
    sessionInfo: sessionModel.$info,
    preference: preferencesFeatureModel.$preference,
  },
  fn: ({ seconds, sessionInfo, preference }, eventData) => ({
    af_content_id: eventData.af_content_id,
    af_content_type: eventData.af_content_type,
    af_content: eventData.af_content,
    minutes_consumed: secondsToRoundedMinutesConsumed(seconds),
    af_customer_user_id: sessionInfo?.userGuid ?? '',
    user_region: preference?.name ?? '',
  }),
  target: logMediaMinutesFx,
})

/** AppsFlyer PBA: tie web events to OVP userGuid (`AF` is queued in index.html until SDK loads). */
const appsFlyerSetCustomerUserIdFx = createEffect((userGuid: string) => {
  if (typeof window === 'undefined') return
  window.AF?.('pba', 'setCustomerUserId', userGuid)
})

sample({
  clock: sessionModel.$info,
  filter: (info) => Boolean(info?.userGuid && info.userGuid.length > 0),
  fn: (info) => info?.userGuid || '',
  target: appsFlyerSetCustomerUserIdFx,
})
